// Typed client for the Worker API (web/src). Errors carry the server message.

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

// The server answers in English; the UI is Russian. Known messages are worded
// here (the same list as the app's core/Errors.kt), the rest pass through.
const RU: Record<string, string> = {
  "login required": "Нужно войти заново",
  "admin only": "Только для админов",
  "demo not found": "Демка не найдена",
  "match not found": "Матч не найден",
  "the demo file for this match has expired": "Файл демки этого матча уже удалён",
  "this player is not in the match": "Этого игрока нет в матче",
  "demo already submitted": "Демка уже отправлена",
  "file is empty or larger than 1 gib": "Файл пустой или больше 1 ГБ",
  "upload not found in storage": "Загрузка не дошла до хранилища — попробуйте ещё раз",
  "the file is not a cs2 demo": "Это не демка CS2",
  "this is a cs:go demo; only cs2 demos are supported": "Это демка CS:GO — поддерживаются только демки CS2",
  "the file is larger than 1 gib": "Файл больше 1 ГБ",
  "monthly video quota used up": "Лимит видео на этот месяц исчерпан",
  "monthly analysis quota used up": "Лимит ИИ-разборов на этот месяц исчерпан",
  "video credits used up": "Видео по гранту закончились",
  "analysis credits used up": "ИИ-разборы по гранту закончились",
  "videos are available with vip or a grant": "Видео доступно с VIP или по гранту",
  "ai analysis is available with vip or a grant": "ИИ-разбор доступен с VIP или по гранту",
  "invalid json body": "Сервер не принял запрос",
  "internal error": "Ошибка сервера — попробуйте позже",
};

export function ruError(message: string): string {
  const m = message.trim();
  if (/[А-Яа-яЁё]/.test(m)) return m;
  const known = RU[m.toLowerCase()];
  if (known) return known;
  const small = /^the file is too small to be a demo \((\d+) bytes\)$/i.exec(m);
  return small ? `Файл слишком маленький для демки (${small[1]} байт)` : m;
}

export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const { json, ...rest } = init;
  const res = await fetch(path, {
    ...rest,
    method: rest.method ?? (json !== undefined ? "POST" : "GET"),
    headers: { "Content-Type": "application/json", ...rest.headers },
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });
  const body = res.headers.get("content-type")?.includes("json") ? await res.json() : null;
  if (!res.ok) throw new ApiError(ruError((body as { error?: string } | null)?.error ?? res.statusText), res.status);
  return body as T;
}

export type User = { id: number; steam_id: string; display_name: string; avatar_url: string | null; role: "user" | "admin"; avatar_animated?: string | null };

export type SteamItems = { avatar?: string; frame?: string; background?: string; backgroundVideo?: { webm?: string; mp4?: string; mp4Small?: string } };

export type Access = { allowed: true; source: string } | { allowed: false; reason: string };
export type Entitlements = Record<"video.kills" | "video.highlights" | "video.mistakes" | "analysis.ai", Access>;

export type DemoRow = {
  id: number; file_name: string; size_bytes: number; status: string; error: string | null; created_at: number;
  match_id: string | null; map: string | null; score_a: number | null; score_b: number | null; source: string | null;
  category: string | null; server_name: string | null; external_match_id: string | null; tag_ids: number[];
  winner_team: "A" | "B" | null; played_at: number | null;
  my_team: "A" | "B" | null; my_kills: number | null; my_deaths: number | null; my_assists: number | null; my_adr: number | null; my_kast: number | null;
};

export type Tag = { id: number; name: string; matches?: number };

export type SearchHit = { steamId: string; name: string; avatar: string | null; matches: number; withYou: number; registered: boolean };

/** A player the profile's player played with or against; wins are the profile player's. */
export type Mate = { steamId: string; name: string; avatar: string | null; matches: number; wins: number };
export type SharedMatch = {
  matchId: string; at: number; map: string; category: string; together: boolean;
  result: "win" | "loss" | "draw"; score: string; kills: number; deaths: number; assists: number;
};

/** A match on the home feed with the viewer's own line (null when they did not play). */
export type FeedMatch = {
  match_id: string; map: string; score_a: number; score_b: number; winner_team: "A" | "B" | null; source: string;
  category: string; server_name: string | null; detail: "full" | "summary"; external_match_id: string | null;
  played_at: number | null; created_at: number; tag_ids: number[];
  my_team: "A" | "B" | null; my_kills: number | null; my_deaths: number | null; my_assists: number | null; my_adr: number | null; my_kast: number | null;
  // Premier / competitive rating: the match average and what it did to you.
  my_rank: number | null; my_rank_change: number | null; avg_rank: number | null;
};
export type FeedSummary = {
  matches: number; wins: number; kills: number; deaths: number; adr: number;
  rank_change: number; best_rank: number | null; form: ("win" | "loss" | "draw")[];
};
/** This week against the one before; null when nothing was played this week. */
export type WeekInsights = {
  matches: number; winRate: number; winRateBefore: number | null; kd: number; kdBefore: number | null;
  adr: number; adrBefore: number | null; rankChange: number; topMistake: { type: MistakeType; n: number } | null;
};
export type SkillKey = "aim" | "movement" | "utility" | "teamplay" | "decisions";
export type FeedSort = "date" | "oldest" | "rating" | "kd" | "adr" | "change";
export type FeedFilter = { category: string | null; map: string | null; result: "win" | "loss" | null; tag: number | null; sort: FeedSort };
export type FeedPage = {
  matches: FeedMatch[]; next: string | null;
  // first page only
  pending?: FeedPending[]; categories?: { category: string; n: number }[]; maps?: { map: string; n: number }[]; summary?: FeedSummary;
  week?: WeekInsights | null;
};
export type FeedPending = { id: number; file_name: string; size_bytes: number; status: string; error: string | null; created_at: number };

export type MatchPlayer = {
  match_id: number; steam_id: string; team_id: "A" | "B"; name: string;
  kills: number; deaths: number; assists: number; headshots: number; damage: number; adr: number; kast: number;
  first_kills: number; first_deaths: number; trade_kills: number; utility_damage: number; flash_assists: number; mvps: number;
  k2: number; k3: number; k4: number; k5: number; rounds_played: number;
  clutches_played: number; clutches_won: number; untraded_deaths: number; utility_thrown: number; enemies_flashed: number; team_flashes: number;
  rank: number; rank_change: number;
  shots: number; hits: number; head_hits: number; shots_moving: number; shots_airborne: number; aimed_shots: number; crosshair_err: number;
  spray_shots: number; spray_hits: number;
};

export type Kill = {
  round: number; tick: number; killer?: string; victim: string; assister?: string; weapon: string;
  headshot?: boolean; wallbang?: boolean; teamKill?: boolean; victimBlind?: boolean; victimUtility?: number; victimTraded?: boolean;
  victimWeapon?: string; killerHealth?: number; aliveMates?: number; aliveEnemies?: number; roundTime?: number;
};
export type Clutch = { round: number; steamId: string; vs: number; won: boolean; kills: number };
export type Round = { number: number; winnerTeam?: "A" | "B"; winnerSide?: "CT" | "T"; reason: string; scoreA: number; scoreB: number };

export type Report = {
  summary: string; strengths: string[]; practice: string[];
  mistakes: { title: string; rounds: number[]; explanation: string; advice: string; severity: "low" | "medium" | "high" }[];
};
export type Analysis = { id: number; steam_id: string; status: "queued" | "running" | "ready" | "failed"; result: Report | null; model: string | null; error: string | null; created_at: number };
export type VideoRow = {
  id: number; steam_id: string; kind: VideoKind; status: string; error: string | null; duration_sec: number | null;
  url: string | null; ordered_by: string | null; height: number;
  waiting: string | null; // queued and waiting for something (an HLAE release, a demo)
};
export type VideoKind = "kills" | "highlights" | "mistakes";

export type MistakeType =
  | "no_trade" | "solo" | "far_from_team" | "slow_rotate" | "no_support" | "team_flash" | "backstab"
  | "aim_low" | "moving_shots" | "spray_miss" | "overflick" | "overpeek" | "rush_no_info" | "eco_mismatch" | "wasted_util" | "jump_shots";

export type PraiseType =
  | "trade" | "opening" | "opening_won" | "clutch" | "multikill" | "flash_assist" | "save" | "clean_spray" | "quick_duel"
  | "util_damage" | "counter_strafe" | "crosshair" | "support";

/** One good moment; round 0 means a habit over the whole match. */
export type Praise = { round: number; tick: number; steamId: string; type: PraiseType; otherId?: string; count?: number; percent?: number };

export type Mistake = {
  round: number; tick: number; steamId: string; type: MistakeType;
  severity: "low" | "medium" | "high"; otherId?: string; seconds?: number; units?: number; count?: number;
};

export type MatchDetail = {
  match: {
    id: string; map: string; source: string; external_match_id: string | null; score_a: number; score_b: number;
    team_a_name: string | null; team_b_name: string | null; winner_team: "A" | "B" | null; duration_sec: number | null;
    played_at: number | null; created_at: number; category: string; server_name: string | null; detail: "full" | "summary"; rounds_count: number; can_edit: 0 | 1;
  };
  tagIds: number[];
  players: MatchPlayer[]; rounds: Round[]; kills: Kill[]; clutches: Clutch[]; mistakes: Mistake[]; praise: Praise[]; videos: VideoRow[]; analyses: Analysis[];
};

export type MatchRow = {
  id: string; map: string; source: string; external_match_id: string | null; score_a: number; score_b: number;
  team_a_name: string | null; team_b_name: string | null; winner_team: string | null; created_at: number;
};

export type MyVideo = {
  id: number; match_id: string | null; steam_id: string; kind: VideoKind; status: string; error: string | null;
  size_bytes: number | null; duration_sec: number | null; created_at: number; player_name: string | null; map: string | null; url: string | null;
  ordered_by: string | null; // set when someone else ordered this video of you
  height: number; // 1080 or 720
};

export type SteamLink = { link: { enabled: number; last_share_code: string; last_checked_at: number | null; last_error: string | null } | null; botSteamId: string | null };

export type PerMatch = { matchId: string; canOpen: boolean; rank: number | null; rankChange: number | null; at: number; map: string; source: string; category: string; detail: "full" | "summary"; result: "win" | "loss" | "draw"; score: string; kills: number; deaths: number; assists: number; adr: number; kast: number; kd: number; hsPercent: number };
export type Totals = {
  matches: number; wins: number; losses: number; winRate: number; rounds: number; kills: number; deaths: number; assists: number;
  kd: number; adr: number; kast: number | null; hsPercent: number; killsPerRound: number; openingKills: number; openingDeaths: number;
  openingWinRate: number; clutchesPlayed: number; clutchesWon: number; untradedDeathRate: number | null; utilityPerRound: number;
  enemiesFlashedPerMatch: number; teamFlashesPerMatch: number | null; utilityDamagePerRound: number; fullMatches: number;
  multiKills: { k2: number; k3: number; k4: number; k5: number }; mvps: number;
};
export type Profile =
  | { empty: true }
  | {
      empty?: undefined;
      player: { steamId: string; name: string; avatar: string | null; faceit: { nickname: string; elo: number | null } | null; items: SteamItems | null };
      filters: { categories: { key: string; count: number }[]; tags: { id: number; name: string; count: number }[] };
      applied: { categories: string[]; tags: number[] };
      teammates: Mate[]; opponents: Mate[];
      withYou: { together: { matches: number; wins: number }; against: { matches: number; wins: number }; matches: SharedMatch[] } | null;
      totals: Totals; clutches: { played: number[]; won: number[] };
      sides: { side: "CT" | "T"; rounds: number; winRate: number; kd: number; kills: number; deaths: number; killsPerRound: number }[];
      weapons: { weapon: string; kills: number; share: number; hsPercent: number }[];
      maps: ({ map: string } & Totals)[]; recent: PerMatch[]; recentTotal: number; trend: PerMatch[];
      rating: { at: number; rank: number; change: number; matchId: string }[];
      decisions: { matches: number; kinds: { type: MistakeType; n: number; perMatch: number }[] };
      strengths: { matches: number; kinds: { type: PraiseType; n: number; perMatch: number }[] };
      skills: {
        scores: Record<SkillKey, number> | null; sample: number; bracket: number; matches: number;
        inputs: { accuracy: number; hsRate: number; crosshairErr: number; sprayAccuracy: number; movingRate: number; enemiesFlashed: number; utilityDamage: number; tradeKills: number; openingWinRate: number };
      } | null;
      analyzedMatches: number; mistakes: { title: string; matches: number; rounds: number; high: number }[];
    };
